<template>
  <!-- Featured Machines Section - Modern Premium Design -->
  <section class="featured-section">
    <div class="featured-container">
      <!-- Section Header with Accent Line -->
      <div class="featured-header">
        <div class="featured-header__content">
          <div class="featured-header__accent"></div>
          <span class="featured-header__badge">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            {{ $t('home.featured.trustBadge') }}
          </span>
          <h2 class="featured-header__title">{{ $t('home.featured.title') }}</h2>
          <p class="featured-header__subtitle">{{ $t('home.featured.subtitle') }}</p>
        </div>

        <!-- View All CTA -->
        <NuxtLink to="/machines" class="featured-header__cta">
          <span>{{ $t('home.featured.viewAll') }}</span>
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </NuxtLink>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="featured-carousel">
        <div class="featured-carousel__track">
          <div v-for="i in 4" :key="i" class="featured-card featured-card--loading">
            <div class="featured-card__image-skeleton"></div>
            <div class="featured-card__content">
              <div class="skeleton-line skeleton-line--sm"></div>
              <div class="skeleton-line skeleton-line--lg"></div>
              <div class="skeleton-line skeleton-line--md"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Machines Carousel -->
      <div v-else-if="featuredMachines.length > 0" class="featured-carousel" @mouseenter="pauseCarousel" @mouseleave="resumeCarousel">
        <!-- Fade edges -->
        <div class="featured-carousel__fade featured-carousel__fade--left"></div>
        <div class="featured-carousel__fade featured-carousel__fade--right"></div>

        <div class="featured-carousel__track" ref="carouselTrack">
          <!-- Double the items for seamless loop -->
          <NuxtLink
            v-for="(machine, index) in [...featuredMachines, ...featuredMachines]"
            :key="`${machine.id}-${index}`"
            :to="`/machines/${machine.id}`"
            class="featured-card"
          >
          <!-- Machine Image -->
          <div class="featured-card__image">
            <img
              v-if="getImageSrc(machine)"
              :src="getImageSrc(machine)"
              :alt="machine.title || machine.name"
              loading="lazy"
              @error="(e) => handleImageError(e)"
            />
            <div v-else class="featured-card__placeholder">
              <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>

            <!-- Overlay Gradient -->
            <div class="featured-card__overlay"></div>

            <!-- Badges Container -->
            <div class="featured-card__badges">
              <span v-if="machine.status" class="featured-badge" :class="getStatusClass(machine.status)">
                {{ getStatusLabel(machine.status) }}
              </span>
              <span v-if="machine.year_manufactured || machine.year" class="featured-badge featured-badge--year">
                {{ machine.year_manufactured || machine.year }}
              </span>
            </div>

            <!-- Quick View Hover -->
            <div class="featured-card__hover">
              <span class="featured-card__hover-text">
                {{ $t('common.viewDetails') }}
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
            </div>
          </div>

          <!-- Machine Details -->
          <div class="featured-card__content">
            <!-- Brand & Category -->
            <div class="featured-card__tags">
              <span v-if="machine.brand" class="featured-tag featured-tag--brand">
                {{ machine.brand.name }}
              </span>
              <span v-if="machine.category" class="featured-tag featured-tag--category">
                {{ machine.category.name }}
              </span>
            </div>

            <!-- Machine Title -->
            <h3 class="featured-card__title">{{ machine.title || machine.name }}</h3>

            <!-- Specs Row: Status & Condition -->
            <div class="featured-card__specs">
              <span v-if="machine.condition" class="featured-spec">
                <span class="featured-spec__dot" :class="machine.condition === 'new' ? 'featured-spec__dot--new' : 'featured-spec__dot--used'"></span>
                {{ machine.condition }}
              </span>
            </div>
          </div>
        </NuxtLink>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="featured-empty">
        <div class="featured-empty__icon">
          <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
        </div>
        <h3 class="featured-empty__title">{{ $t('home.featured.noMachines') }}</h3>
      </div>

    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const { getSafeImageUrl, handleImageError } = useImageHandling();

const loading = ref(true);
const featuredMachines = ref([]);
const carouselTrack = ref(null);
const isPaused = ref(false);

// Carousel control
const pauseCarousel = () => {
  isPaused.value = true;
  if (carouselTrack.value) {
    carouselTrack.value.style.animationPlayState = 'paused';
  }
};

const resumeCarousel = () => {
  isPaused.value = false;
  if (carouselTrack.value) {
    carouselTrack.value.style.animationPlayState = 'running';
  }
};

// Get image source from machine object (handles different API response structures)
// Uses the shared getSafeImageUrl composable so all URL formats are rewritten
// to relative /storage/ paths, correctly routed through Nuxt's proxy.
const getImageSrc = (machine) => {
  if (!machine.images || machine.images.length === 0) return null;
  const image = machine.images[0];
  // Prefer file_path (raw relative path) first, then fall back to full url
  const path = image.file_path || image.url || image.full_url;
  if (!path) return null;
  return getSafeImageUrl(path);
};

const getStatusClass = (status) => {
  const statusValue = typeof status === 'object' ? status.value : status;
  const statusMap = {
    'available': 'featured-badge--available',
    'published': 'featured-badge--available',
    'reserved': 'featured-badge--reserved',
    'sold': 'featured-badge--sold',
    'draft': 'featured-badge--draft'
  };
  return statusMap[statusValue] || 'featured-badge--draft';
};

const getStatusLabel = (status) => {
  const { t } = useI18n();
  const statusValue = typeof status === 'object' ? status.value : status;
  const statusKey = `machines.status.${statusValue}`;
  const translated = t(statusKey);
  if (translated === statusKey) {
    return statusValue ? statusValue.charAt(0).toUpperCase() + statusValue.slice(1) : '';
  }
  return translated;
};

const fetchFeaturedMachines = async () => {
  try {
    loading.value = true;
    const config = useRuntimeConfig();
    const apiBase = config.public?.apiBase || '/api/v1';

    // Try featured endpoint first, fallback to general machines
    try {
      const response = await $fetch(`${apiBase}/machines/featured`);
      featuredMachines.value = response.data || response || [];
    } catch {
      // Fallback to general machines endpoint
      const response = await $fetch(`${apiBase}/machines`, {
        query: { limit: 6, sort: '-created_at' }
      });
      featuredMachines.value = response.data || [];
    }
  } catch (error) {
    console.error('Error fetching featured machines:', error);
    featuredMachines.value = [];
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchFeaturedMachines();
});
</script>

<style scoped>
/* Section Layout */
.featured-section {
  padding: 80px 0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  position: relative;
}

.featured-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header Styles */
.featured-header {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 48px;
}

@media (min-width: 768px) {
  .featured-header {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
}

.featured-header__content {
  max-width: 600px;
}

.featured-header__accent {
  width: 48px;
  height: 4px;
  background: linear-gradient(90deg, #2563eb 0%, #1d4ed8 100%);
  border-radius: 2px;
  margin-bottom: 16px;
}

.featured-header__badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 600;
  border-radius: 20px;
  margin-bottom: 16px;
  letter-spacing: 0.02em;
}

.featured-header__title {
  font-size: 36px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 12px 0;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.featured-header__subtitle {
  font-size: 18px;
  color: #64748b;
  margin: 0;
  line-height: 1.6;
}

.featured-header__cta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  background: #0f172a;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.15);
}

.featured-header__cta:hover {
  background: #1e293b;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(15, 23, 42, 0.2);
}

/* Grid Layout */
.featured-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

@media (min-width: 640px) {
  .featured-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .featured-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Carousel Layout */
.featured-carousel {
  position: relative;
  overflow: hidden;
  margin: 0 -24px;
  padding: 20px 0;
}

.featured-carousel__fade {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 80px;
  z-index: 2;
  pointer-events: none;
}

.featured-carousel__fade--left {
  left: 0;
  background: linear-gradient(90deg, #f8fafc 0%, transparent 100%);
}

.featured-carousel__fade--right {
  right: 0;
  background: linear-gradient(-90deg, #f1f5f9 0%, transparent 100%);
}

.featured-carousel__track {
  display: flex;
  gap: 24px;
  padding: 0 24px;
  animation: carouselScroll 30s linear infinite;
  width: fit-content;
}

@media (max-width: 640px) {
  .featured-carousel__track {
    animation-duration: 15s;
  }
}

@keyframes carouselScroll {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

/* Card Styles */
.featured-card {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 320px;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.02);
  border: 1px solid #e2e8f0;
}

@media (min-width: 768px) {
  .featured-card {
    width: 360px;
  }
}

.featured-card:hover {
  transform: translateY(-4px) scale(1.01);
  box-shadow: 0 15px 35px -8px rgba(0, 0, 0, 0.15);
  border-color: #3b82f6;
}

.featured-card--loading {
  pointer-events: none;
  width: 320px;
}

@media (min-width: 768px) {
  .featured-card--loading {
    width: 360px;
  }
}

.featured-card__image-skeleton {
  height: 200px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton-line {
  height: 16px;
  background: #f1f5f9;
  border-radius: 8px;
  margin-bottom: 12px;
}

.skeleton-line--sm { width: 30%; }
.skeleton-line--md { width: 50%; }
.skeleton-line--lg { width: 80%; }

/* Card Image */
.featured-card__image {
  position: relative;
  height: 200px;
  overflow: hidden;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.featured-card__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.featured-card:hover .featured-card__image img {
  transform: scale(1.03);
}

.featured-card__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
}

.featured-card__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 40%, rgba(0, 0, 0, 0.6) 100%);
  opacity: 0;
  transition: opacity 0.4s ease;
}

.featured-card:hover .featured-card__overlay {
  opacity: 1;
}

/* Badges */
.featured-card__badges {
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.featured-badge {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
  backdrop-filter: blur(8px);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.featured-badge--available {
  background: rgba(34, 197, 94, 0.9);
  color: #ffffff;
}

.featured-badge--reserved {
  background: rgba(245, 158, 11, 0.9);
  color: #ffffff;
}

.featured-badge--sold {
  background: rgba(239, 68, 68, 0.9);
  color: #ffffff;
}

.featured-badge--draft {
  background: rgba(100, 116, 139, 0.9);
  color: #ffffff;
}

.featured-badge--year {
  background: rgba(255, 255, 255, 0.95);
  color: #334155;
  margin-left: auto;
}

/* Hover View */
.featured-card__hover {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%) translateY(20px);
  opacity: 0;
  transition: all 0.4s ease;
}

.featured-card:hover .featured-card__hover {
  transform: translateX(-50%) translateY(0);
  opacity: 1;
}

.featured-card__hover-text {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #ffffff;
  color: #1e40af;
  font-size: 14px;
  font-weight: 600;
  border-radius: 25px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

/* Card Content */
.featured-card__content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.featured-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.featured-tag {
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
}

.featured-tag--brand {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1d4ed8;
}

.featured-tag--category {
  background: #f1f5f9;
  color: #475569;
}

.featured-card__title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 16px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.3s ease;
}

.featured-card:hover .featured-card__title {
  color: #2563eb;
}

.featured-card__specs {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}

.featured-spec {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #64748b;
  text-transform: capitalize;
}

.featured-spec--location {
  color: #64748b;
}

.featured-spec__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.featured-spec__dot--new {
  background: #22c55e;
  box-shadow: 0 0 8px rgba(34, 197, 94, 0.4);
}

.featured-spec__dot--used {
  background: #f59e0b;
  box-shadow: 0 0 8px rgba(245, 158, 11, 0.4);
}

/* Empty State */
.featured-empty {
  text-align: center;
  padding: 80px 40px;
  background: #ffffff;
  border-radius: 24px;
  border: 2px dashed #e2e8f0;
}

.featured-empty__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: #f8fafc;
  border-radius: 50%;
  margin-bottom: 24px;
  color: #cbd5e1;
}

.featured-empty__title {
  font-size: 20px;
  font-weight: 700;
  color: #334155;
  margin: 0 0 8px 0;
}

.featured-empty__text {
  font-size: 16px;
  color: #64748b;
  margin: 0;
}

/* Responsive */
@media (max-width: 640px) {
  .featured-section {
    padding: 60px 0;
  }

  .featured-header__title {
    font-size: 28px;
  }

  .featured-header__subtitle {
    font-size: 16px;
  }

  .featured-card__image {
    height: 180px;
  }

  .featured-card__content {
    padding: 20px;
  }
}
</style>
